/**
 * Shared utilities for RiskScoreScale components (V3 and non-V3).
 * Contains types, constants, and helper functions used by both versions.
 */

export type RiskClassification = 'Benign' | 'Inconclusive' | 'Risky';

export interface RiskScoreScaleProps {
  /** Composite Risk Score from -1 to 1 */
  score: number;
  /** Classification from the risk scoring tool */
  classification: RiskClassification;
  /** Threshold below which scores are classified as Benign */
  benignThreshold: number;
  /** Threshold above which scores are classified as Risky */
  riskyThreshold: number;
}

export const CLASSIFICATION_COLORS = {
  Benign: 'green-400',
  Inconclusive: 'yellow-500',
  Risky: 'red-300',
} as const;

/**
 * Converts a score from [-1, 1] range to percentage [0, 100] for CSS positioning
 */
export const scoreToPercentage = (score: number): number => {
  const clampedScore = Math.max(-1, Math.min(1, score));
  return ((clampedScore + 1) / 2) * 100;
};

/**
 * Pulse animation for the risk score marker.
 * Used via the sx prop on the marker element.
 */
export const PULSE_ANIMATION_SX = {
  '@keyframes pulse': {
    '0%': {
      transform: 'translateX(-50%) scale(1)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    },
    '50%': {
      transform: 'translateX(-50%) scale(1.3)',
      boxShadow: '0 0 20px rgba(255,255,255,0.6)',
    },
    '100%': {
      transform: 'translateX(-50%) scale(1)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    },
  },
  animation: 'pulse 1.2s ease-out',
};
